import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Package, Users, ShoppingBag, DollarSign, 
  Plus, Edit, Trash2, Save, X, Eye, Settings, Warehouse, Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    fetchData();
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, productsRes, ordersRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Admin fetch error:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  if (authLoading || !isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Eye },
    { id: "products", label: "Products", icon: Package },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "notifications", label: "Waitlist", icon: Users },
    { id: "inventory", label: "Inventory", icon: Warehouse },
    { id: "users", label: "Users", icon: Users },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
            ADMIN PANEL
          </p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">
            DASHBOARD
          </h1>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-sm tracking-wider uppercase whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-[#FFD700] text-black"
                  : "bg-[#1A1A1A] text-white hover:bg-[#262626]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loader" />
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && stats && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <StatCard
                  icon={DollarSign}
                  label="Total Revenue"
                  value={`A$${stats.total_revenue.toFixed(2)}`}
                  color="text-green-500"
                />
                <StatCard
                  icon={ShoppingBag}
                  label="Total Orders"
                  value={stats.total_orders}
                  subtext={`${stats.pending_orders} pending`}
                />
                <StatCard
                  icon={Package}
                  label="Products"
                  value={stats.total_products}
                />
                <StatCard
                  icon={Users}
                  label="Customers"
                  value={stats.total_users}
                />
              </motion.div>
            )}

            {/* Products Tab */}
            {activeTab === "products" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-2xl">MANAGE PRODUCTS</h2>
                  <button
                    onClick={() => {
                      setEditingProduct(null);
                      setShowProductForm(true);
                    }}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>

                {showProductForm && (
                  <ProductForm
                    product={editingProduct}
                    token={token}
                    onClose={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                    }}
                    onSave={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                      fetchData();
                    }}
                  />
                )}

                <div className="space-y-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="p-5 border border-white/10 hover:border-[#FFD700]/30 transition-colors"
                      data-testid={`admin-product-${product.id}`}
                    >
                      <div className="flex items-start gap-5">
                        {/* Images */}
                        <div className="flex gap-2 flex-shrink-0">
                          {product.images?.slice(0, 2).map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`${product.name} ${idx === 0 ? 'front' : 'back'}`}
                              className="w-16 h-20 object-cover bg-[#1A1A1A] border border-white/10"
                            />
                          ))}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg">{product.name}</h3>
                          <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-1">{product.description}</p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            <span className="font-mono text-[#FFD700]">A${product.price?.toFixed(2)}</span>
                            <span className="text-[#A3A3A3] text-sm font-mono">Sizes: {product.sizes?.join(', ')}</span>
                            <span className="text-[#A3A3A3] text-sm font-mono">Stock: {product.stock_by_size ? Object.entries(product.stock_by_size).map(([s, q]) => `${s}:${q}`).join(' / ') : product.stock}</span>
                            {product.sold_count > 0 && (
                              <span className="text-[#A3A3A3] text-sm font-mono">{product.sold_count} sold</span>
                            )}
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span className={`px-2 py-1 text-xs font-mono ${product.featured ? 'bg-[#FFD700] text-black' : 'bg-[#1A1A1A]'}`}>
                            {product.featured ? 'FEATURED' : 'REGULAR'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
                        <button
                          onClick={() => {
                            setEditingProduct(product);
                            setShowProductForm(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black font-mono text-xs tracking-wider hover:bg-[#FFC000] transition-colors"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                          EDIT PRODUCT
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="flex items-center gap-2 px-4 py-2 border border-white/20 font-mono text-xs tracking-wider hover:border-red-500 hover:text-red-500 transition-colors"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          DELETE
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Orders Tab */}
            {activeTab === "orders" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl mb-6">MANAGE ORDERS</h2>
                
                {orders.length === 0 ? (
                  <p className="text-[#A3A3A3] text-center py-12">No orders yet</p>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className="p-6 border border-white/10"
                        data-testid={`admin-order-${order.id}`}
                      >
                        <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
                          <div>
                            <p className="font-mono text-xs text-[#A3A3A3]">ORDER ID</p>
                            <p className="font-mono">{order.id.slice(0, 8).toUpperCase()}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-[#A3A3A3]">CUSTOMER</p>
                            <p>{order.guest_name || order.user?.name || "Unknown"}</p>
                            <p className="text-sm text-[#A3A3A3]">{order.guest_email || order.user?.email}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-[#A3A3A3]">DATE</p>
                            <p className="font-mono">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-[#A3A3A3]">TOTAL</p>
                            <p className="font-mono text-[#FFD700] text-lg">A${order.total?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="font-mono text-xs text-[#A3A3A3] mb-1">STATUS</p>
                            <select
                              value={order.status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                              className="bg-[#1A1A1A] border border-white/20 px-3 py-2 font-mono text-sm"
                              data-testid={`order-status-${order.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="border-t border-white/10 pt-4 mt-4">
                          <p className="font-mono text-xs text-[#A3A3A3] mb-2">ITEMS</p>
                          <div className="space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{item.product_name} x{item.quantity} ({item.size}, {item.color})</span>
                                <span className="font-mono">A${(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {order.shipping_address && (
                          <div className="border-t border-white/10 pt-4 mt-4">
                            <p className="font-mono text-xs text-[#A3A3A3] mb-2">SHIPPING ADDRESS</p>
                            <p className="text-sm">{order.shipping_address.name}</p>
                            <p className="text-sm text-[#A3A3A3]">{order.shipping_address.address}</p>
                            <p className="text-sm text-[#A3A3A3]">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}


            {/* Waitlist Tab */}
            {activeTab === "notifications" && (
              <WaitlistPanel token={token} />
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl mb-6">CUSTOMERS</h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-4 px-4 font-mono text-xs text-[#A3A3A3] uppercase">Name</th>
                        <th className="text-left py-4 px-4 font-mono text-xs text-[#A3A3A3] uppercase">Email</th>
                        <th className="text-left py-4 px-4 font-mono text-xs text-[#A3A3A3] uppercase">Role</th>
                        <th className="text-left py-4 px-4 font-mono text-xs text-[#A3A3A3] uppercase">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b border-white/10 hover:bg-white/5" data-testid={`admin-user-${u.id}`}>
                          <td className="py-4 px-4">{u.name}</td>
                          <td className="py-4 px-4 font-mono text-sm">{u.email}</td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 text-xs font-mono ${u.role === 'admin' ? 'bg-[#FFD700] text-black' : 'bg-[#1A1A1A]'}`}>
                              {u.role?.toUpperCase() || 'USER'}
                            </span>
                          </td>
                          <td className="py-4 px-4 font-mono text-sm text-[#A3A3A3]">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Inventory Tab */}
            {activeTab === "inventory" && (
              <InventoryPanel token={token} products={products} />
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <SettingsPanel token={token} user={user} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="p-6 border border-white/10 hover:border-[#FFD700]/30 transition-colors">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center">
        <Icon className={`w-6 h-6 ${color || 'text-[#FFD700]'}`} />
      </div>
      <div>
        <p className="font-mono text-xs text-[#A3A3A3] uppercase">{label}</p>
        <p className="font-heading text-2xl">{value}</p>
        {subtext && <p className="text-sm text-[#A3A3A3]">{subtext}</p>}
      </div>
    </div>
  </div>
);

// Product Form Component
const ProductForm = ({ product, token, onClose, onSave }) => {
  const sizesStr = product?.sizes?.join(", ") || "M, L, XL";
  const initialStockBySize = product?.stock_by_size || {};
  
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || "",
    category: product?.category || "tanks",
    sizes: sizesStr,
    colors: product?.colors?.join(", ") || "Charcoal",
    images: product?.images?.join("\n") || "",
    featured: product?.featured || false,
  });
  const [stockBySize, setStockBySize] = useState(initialStockBySize);
  const [soldCount, setSoldCount] = useState(product?.sold_count || 0);
  const [saving, setSaving] = useState(false);

  const currentSizes = formData.sizes.split(",").map((s) => s.trim()).filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const sizes = formData.sizes.split(",").map((s) => s.trim()).filter(Boolean);
    const sbs = {};
    sizes.forEach((s) => { sbs[s] = parseInt(stockBySize[s]) || 0; });
    const totalStock = Object.values(sbs).reduce((a, b) => a + b, 0);

    const data = {
      ...formData,
      price: parseFloat(formData.price),
      stock: totalStock,
      stock_by_size: sbs,
      sold_count: parseInt(soldCount) || 0,
      sizes,
      colors: formData.colors.split(",").map((c) => c.trim()).filter(Boolean),
      images: formData.images.split("\n").map((i) => i.trim()).filter(Boolean),
    };

    try {
      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product created");
      }
      onSave();
    } catch (error) {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0A0A0A] border border-white/10 p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-2xl">
            {product ? "EDIT PRODUCT" : "ADD PRODUCT"}
          </h2>
          <button onClick={onClose} className="p-2 hover:text-[#FFD700]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-underline"
              required
              data-testid="product-name-input"
            />
          </div>

          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-underline min-h-[100px] resize-none"
              required
              data-testid="product-description-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Price (A$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="input-underline"
                required
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Total Stock</label>
              <p className="font-mono text-lg text-[#FFD700]">{currentSizes.reduce((t, s) => t + (parseInt(stockBySize[s]) || 0), 0)}</p>
            </div>
            <div>
              <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Sold Count</label>
              <input
                type="number"
                min="0"
                value={soldCount}
                onChange={(e) => setSoldCount(e.target.value)}
                className="input-underline"
                data-testid="product-sold-count-input"
              />
            </div>
          </div>

          {/* Stock by Size */}
          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-3">STOCK BY SIZE</label>
            <div className="grid grid-cols-3 gap-4">
              {currentSizes.map((size) => (
                <div key={size} className="border border-white/10 p-3">
                  <label className="font-mono text-xs text-[#FFD700] block mb-2">{size}</label>
                  <input
                    type="number"
                    min="0"
                    value={stockBySize[size] || 0}
                    onChange={(e) => setStockBySize({ ...stockBySize, [size]: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 py-2 text-white font-mono focus:border-[#FFD700] focus:outline-none"
                    data-testid={`stock-size-${size}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Sizes (comma separated)</label>
            <input
              type="text"
              value={formData.sizes}
              onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
              className="input-underline"
              placeholder="S, M, L, XL"
              data-testid="product-sizes-input"
            />
          </div>

          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Colors (comma separated)</label>
            <input
              type="text"
              value={formData.colors}
              onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
              className="input-underline"
              placeholder="Black, Charcoal, White"
              data-testid="product-colors-input"
            />
          </div>

          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Image URLs (one per line)</label>
            <textarea
              value={formData.images}
              onChange={(e) => setFormData({ ...formData, images: e.target.value })}
              className="input-underline min-h-[80px] resize-none font-mono text-sm"
              placeholder="https://example.com/image1.jpg"
              data-testid="product-images-input"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="w-5 h-5 accent-[#FFD700]"
              data-testid="product-featured-input"
            />
            <label htmlFor="featured" className="font-mono text-sm">Featured Product</label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="save-product-btn"
            >
              {saving ? (
                <div className="loader w-5 h-5 border-2" />
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {product ? "Update" : "Create"} Product
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};


// Settings Panel Component
const SettingsPanel = ({ token, user }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [resetting, setResetting] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPw(true);
    try {
      await axios.put(
        `${API}/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <h2 className="font-heading text-2xl">SETTINGS</h2>

      {/* Account Info */}
      <div className="border border-white/10 p-8">
        <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#FFD700]" />
          ACCOUNT INFO
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-3 border-b border-white/10">
            <span className="font-mono text-xs text-[#A3A3A3] uppercase">Email</span>
            <span className="font-mono text-sm">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-white/10">
            <span className="font-mono text-xs text-[#A3A3A3] uppercase">Role</span>
            <span className="px-2 py-1 text-xs font-mono bg-[#FFD700] text-black">ADMIN</span>
          </div>
          <div className="flex justify-between items-center py-3">
            <span className="font-mono text-xs text-[#A3A3A3] uppercase">Name</span>
            <span className="font-mono text-sm">{user?.name}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="border border-white/10 p-8">
        <h3 className="font-heading text-lg mb-6">CHANGE PASSWORD</h3>
        <form onSubmit={handleChangePassword} className="space-y-5 max-w-md">
          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none transition-colors font-mono"
              required
              data-testid="current-password"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none transition-colors font-mono"
              required
              minLength={6}
              data-testid="new-password"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-[#A3A3A3] uppercase block mb-2">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none transition-colors font-mono"
              required
              minLength={6}
              data-testid="confirm-password"
            />
          </div>
          <button
            type="submit"
            disabled={savingPw}
            className="bg-[#FFD700] text-black font-heading py-3 px-8 tracking-wider hover:bg-[#FFC000] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="change-password-btn"
          >
            {savingPw ? "SAVING..." : "CHANGE PASSWORD"}
          </button>
        </form>
      </div>

      {/* Reset Data */}
      <div className="border border-red-500/30 p-8">
        <h3 className="font-heading text-lg mb-2 text-red-500">DANGER ZONE</h3>
        <p className="text-[#A3A3A3] text-sm mb-6">These actions are irreversible. Use before going live to clean up test data.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-white/10">
            <div>
              <p className="font-mono text-sm">Delete All Orders</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Removes all orders, payments and resets sold counts</p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("DELETE ALL ORDERS? This cannot be undone.")) return;
                setResetting("orders");
                try {
                  const res = await axios.delete(`${API}/admin/reset/orders`, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(res.data.message);
                } catch { toast.error("Failed to reset orders"); }
                finally { setResetting(""); }
              }}
              disabled={!!resetting}
              className="px-4 py-2 border border-red-500 text-red-500 font-mono text-xs tracking-wider hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
              data-testid="reset-orders-btn"
            >
              {resetting === "orders" ? "DELETING..." : "DELETE ORDERS"}
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-white/10">
            <div>
              <p className="font-mono text-sm">Delete Test Users</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Removes all non-admin users, carts and wishlists</p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("DELETE ALL TEST USERS? This cannot be undone.")) return;
                setResetting("users");
                try {
                  const res = await axios.delete(`${API}/admin/reset/test-users`, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(res.data.message);
                } catch { toast.error("Failed to reset users"); }
                finally { setResetting(""); }
              }}
              disabled={!!resetting}
              className="px-4 py-2 border border-red-500 text-red-500 font-mono text-xs tracking-wider hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
              data-testid="reset-users-btn"
            >
              {resetting === "users" ? "DELETING..." : "DELETE USERS"}
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-mono text-sm text-red-500 font-bold">Reset Everything</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Deletes all orders, payments, test users, carts and wishlists. Keeps admin and products.</p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("RESET ALL TEST DATA? This will delete ALL orders and ALL non-admin users. This cannot be undone.")) return;
                if (!window.confirm("Are you absolutely sure? Type YES in the next prompt.")) return;
                setResetting("all");
                try {
                  const res = await axios.delete(`${API}/admin/reset/all`, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(res.data.message);
                } catch { toast.error("Failed to reset data"); }
                finally { setResetting(""); }
              }}
              disabled={!!resetting}
              className="px-4 py-2 bg-red-500 text-white font-mono text-xs tracking-wider hover:bg-red-600 transition-colors disabled:opacity-50"
              data-testid="reset-all-btn"
            >
              {resetting === "all" ? "RESETTING..." : "RESET EVERYTHING"}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};


// Waitlist Panel Component
const WaitlistPanel = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="loader" /></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl">STOCK WAITLIST</h2>
        <span className="font-mono text-sm text-[#FFD700]">{notifications.length} request{notifications.length !== 1 ? 's' : ''}</span>
      </div>

      {notifications.length === 0 ? (
        <div className="border border-white/10 p-12 text-center">
          <p className="text-[#A3A3A3] font-mono">No waitlist requests yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((n) => (
            <div key={n.id} className="flex items-center justify-between p-4 border border-white/10 hover:border-[#FFD700]/30 transition-colors">
              <div>
                <p className="font-mono text-sm text-white">{n.email}</p>
                <p className="text-[#A3A3A3] text-xs mt-1">{n.product_name}</p>
              </div>
              <p className="font-mono text-xs text-[#A3A3A3]">{new Date(n.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};


// Inventory Panel Component
const InventoryPanel = ({ token, products }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inventory, setInventory] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifyReport, setVerifyReport] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [productAudit, setProductAudit] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [enforcementSetting, setEnforcementSetting] = useState(null);
  const [reason, setReason] = useState("manual_adjustment");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchEnforcement();
    fetchDbInfo();
  }, []);

  const fetchEnforcement = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory/settings`, { headers });
      setEnforcementSetting(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchDbInfo = async () => {
    try {
      const res = await axios.get(`${API}/admin/system/db-info`, { headers });
      setDbInfo(res.data);
    } catch (e) { console.error(e); }
  };

  const loadProductInventory = async (product) => {
    setSelectedProduct(product);
    setProductAudit(null);
    try {
      const res = await axios.get(`${API}/admin/products/${product.id}/inventory`, { headers });
      const inv = res.data.inventory_by_variant || {};
      // Build full grid from product colors/sizes, fill with existing values
      const grid = {};
      (product.colors || []).forEach(c => {
        grid[c] = {};
        (product.sizes || []).forEach(s => {
          grid[c][s] = inv[c]?.[s] ?? "";
        });
      });
      setInventory(grid);
    } catch (e) {
      toast.error("Failed to load inventory");
    }
  };

  const handleSave = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    // Convert empty strings to 0, validate
    const cleaned = {};
    let hasValues = false;
    for (const [color, sizes] of Object.entries(inventory)) {
      cleaned[color] = {};
      for (const [size, val] of Object.entries(sizes)) {
        const num = val === "" ? 0 : parseInt(val, 10);
        if (isNaN(num) || num < 0) {
          toast.error(`Invalid quantity for ${color} / ${size}`);
          setSaving(false);
          return;
        }
        cleaned[color][size] = num;
        if (num > 0) hasValues = true;
      }
    }
    try {
      await axios.put(`${API}/admin/products/${selectedProduct.id}/inventory`, {
        inventory_by_variant: cleaned,
        reason: reason || "manual_adjustment"
      }, { headers });
      toast.success("Inventory saved");
      setReason("manual_adjustment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const loadProductAudit = async (productId) => {
    try {
      const res = await axios.get(`${API}/admin/products/${productId}/inventory-log`, { headers });
      setProductAudit(res.data);
    } catch (e) { toast.error("Failed to load audit log"); }
  };

  const runVerification = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory/verify`, { headers });
      setVerifyReport(res.data);
    } catch (e) { toast.error("Verification failed"); }
  };

  const loadFullAudit = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory/audit-log`, { headers });
      setAuditLog(res.data);
      setShowAudit(true);
    } catch (e) { toast.error("Failed to load audit log"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8" data-testid="inventory-panel">
      {/* Enforcement Status */}
      <div className="p-6 border border-white/10 bg-[#0A0A0A]">
        <div className="flex items-center gap-3 mb-3">
          <Lock className="w-5 h-5 text-[#A3A3A3]" />
          <h3 className="font-heading text-lg">INVENTORY ENFORCEMENT</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full bg-[#A3A3A3]" />
          <span className="font-mono text-sm text-[#A3A3A3]">DISABLED — LOCKED</span>
        </div>
        <p className="text-[#666] text-xs font-mono mt-2">
          Inventory enforcement unavailable until production inventory, transaction support and active reservation logic are verified.
        </p>
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="flex items-center gap-3">
            <span className={`inline-block w-3 h-3 rounded-full ${enforcementSetting?.setup?.value ? "bg-green-500" : "bg-[#A3A3A3]"}`} />
            <span className="font-mono text-sm text-[#A3A3A3]">
              INVENTORY SETUP: {enforcementSetting?.setup?.value ? "UNLOCKED — Ready for physical count" : "LOCKED — Read-only mode"}
            </span>
          </div>
        </div>
      </div>

      {/* DB Info */}
      {dbInfo && (
        <div className="p-4 border border-white/10">
          <p className="font-mono text-xs text-[#A3A3A3] mb-2">MONGODB DIAGNOSTICS</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs">
            <div><span className="text-[#666]">Version:</span> <span className="text-white">{dbInfo.server_version}</span></div>
            <div><span className="text-[#666]">Topology:</span> <span className="text-white">{dbInfo.topology}</span></div>
            <div><span className="text-[#666]">Transactions:</span> <span className={dbInfo.transactions_supported ? "text-green-500" : "text-red-500"}>{dbInfo.transactions_supported ? "Supported" : "Not Supported"}</span></div>
            <div><span className="text-[#666]">Database:</span> <span className="text-white">{dbInfo.database}</span></div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <button onClick={runVerification} className="px-4 py-2 border border-white/20 font-mono text-xs tracking-wider hover:border-[#FFD700] hover:text-[#FFD700] transition-colors" data-testid="verify-inventory-btn">
          VERIFICATION REPORT
        </button>
        <button onClick={loadFullAudit} className="px-4 py-2 border border-white/20 font-mono text-xs tracking-wider hover:border-[#FFD700] hover:text-[#FFD700] transition-colors" data-testid="audit-log-btn">
          FULL AUDIT LOG
        </button>
      </div>

      {/* Verification Report */}
      {verifyReport && (
        <div className="p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-sm text-[#FFD700]">INVENTORY VERIFICATION REPORT</p>
            <button onClick={() => setVerifyReport(null)} className="text-[#A3A3A3] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <p className={`font-mono text-sm mb-4 ${verifyReport.all_valid ? "text-green-500" : "text-red-500"}`}>
            {verifyReport.all_valid ? "ALL PRODUCTS VERIFIED" : "ISSUES FOUND"}
          </p>
          <div className="space-y-4">
            {verifyReport.products.map(p => (
              <div key={p.product_id} className="border border-white/5 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-heading text-sm">{p.product}</p>
                  <span className={`font-mono text-xs ${p.status === "NOT_INITIALIZED" ? "text-[#A3A3A3]" : p.match ? "text-green-500" : "text-red-500"}`}>
                    {p.status === "NOT_INITIALIZED" ? "NOT SET" : p.match ? "MATCH" : "MISMATCH"}
                  </span>
                </div>
                {p.variants.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-2">
                    {p.variants.map((v, i) => (
                      <span key={i} className="font-mono text-xs text-[#A3A3A3]">{v.color}/{v.size}: {v.stock}</span>
                    ))}
                  </div>
                )}
                <p className="font-mono text-xs text-[#666] mt-1">Sum: {p.variant_sum} | Total stock: {p.total_stock}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full Audit Log */}
      {showAudit && (
        <div className="p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-sm text-[#FFD700]">FULL AUDIT LOG</p>
            <button onClick={() => setShowAudit(false)} className="text-[#A3A3A3] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {auditLog.length === 0 ? (
            <p className="text-[#A3A3A3] font-mono text-sm">No audit entries yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="text-[#A3A3A3] border-b border-white/10">
                    <th className="text-left p-2">Time</th>
                    <th className="text-left p-2">Product</th>
                    <th className="text-left p-2">Variant</th>
                    <th className="text-left p-2">Before</th>
                    <th className="text-left p-2">After</th>
                    <th className="text-left p-2">Change</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-left p-2">Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((l, i) => (
                    <tr key={i} className="border-b border-white/5 text-[#ccc]">
                      <td className="p-2">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="p-2">{l.product_name?.substring(0, 25)}</td>
                      <td className="p-2">{l.color}/{l.size}</td>
                      <td className="p-2">{l.before}</td>
                      <td className="p-2">{l.after}</td>
                      <td className={`p-2 ${l.change > 0 ? "text-green-500" : l.change < 0 ? "text-red-500" : ""}`}>{l.change > 0 ? "+" : ""}{l.change}</td>
                      <td className="p-2">{l.reason}</td>
                      <td className="p-2">{l.admin_email?.split("@")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Product List */}
      <div>
        <p className="font-mono text-sm text-[#FFD700] mb-4">SELECT PRODUCT TO MANAGE INVENTORY</p>
        <div className="space-y-2">
          {products.map(p => (
            <button
              key={p.id}
              onClick={() => loadProductInventory(p)}
              className={`w-full text-left p-4 border transition-colors ${
                selectedProduct?.id === p.id ? "border-[#FFD700] bg-[#1A1A1A]" : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`inv-product-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading text-sm">{p.name}</p>
                  <p className="font-mono text-xs text-[#A3A3A3] mt-1">
                    Colors: {(p.colors || []).join(", ")} | Sizes: {(p.sizes || []).join(", ")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{p.inventory_by_variant ? "INITIALIZED" : "NOT SET"}</p>
                  <p className="font-mono text-xs text-[#A3A3A3]">Stock: {p.stock}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Inventory Editor */}
      {selectedProduct && (
        <div className="p-6 border border-[#FFD700]/30 bg-[#0A0A0A]" data-testid="inventory-editor">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-mono text-xs text-[#FFD700] mb-1">EDITING INVENTORY</p>
              <p className="font-heading text-xl">{selectedProduct.name}</p>
            </div>
            <button onClick={() => setSelectedProduct(null)} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {/* Grid: colors as rows, sizes as columns */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left font-mono text-xs text-[#A3A3A3] p-2">COLOR / SIZE</th>
                  {(selectedProduct.sizes || []).map(s => (
                    <th key={s} className="text-center font-mono text-xs text-[#FFD700] p-2">{s}</th>
                  ))}
                  <th className="text-center font-mono text-xs text-[#A3A3A3] p-2">ROW TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(selectedProduct.colors || []).map(color => {
                  const rowTotal = (selectedProduct.sizes || []).reduce((sum, s) => {
                    const v = inventory[color]?.[s];
                    return sum + (v === "" ? 0 : parseInt(v, 10) || 0);
                  }, 0);
                  return (
                    <tr key={color} className="border-t border-white/5">
                      <td className="font-mono text-sm p-2 text-white">{color}</td>
                      {(selectedProduct.sizes || []).map(size => (
                        <td key={size} className="p-2 text-center">
                          <input
                            type="number"
                            min="0"
                            value={inventory[color]?.[size] ?? ""}
                            onChange={e => {
                              const val = e.target.value;
                              setInventory(prev => ({
                                ...prev,
                                [color]: { ...prev[color], [size]: val === "" ? "" : val }
                              }));
                            }}
                            className="w-16 bg-[#111] border border-white/20 text-center text-white font-mono text-sm py-2 focus:border-[#FFD700] focus:outline-none"
                            data-testid={`inv-input-${color}-${size}`}
                          />
                        </td>
                      ))}
                      <td className="text-center font-mono text-sm text-[#A3A3A3] p-2">{rowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Grand total */}
          <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
            <p className="font-mono text-sm text-[#A3A3A3]">
              TOTAL: {Object.values(inventory).reduce((sum, sizes) =>
                sum + Object.values(sizes).reduce((s, v) => s + (v === "" ? 0 : parseInt(v, 10) || 0), 0), 0
              )}
            </p>
          </div>

          {/* Reason */}
          <div className="mt-4">
            <label className="font-mono text-xs text-[#A3A3A3] block mb-1">REASON FOR CHANGE</label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="bg-[#111] border border-white/20 text-white font-mono text-sm py-2 px-3 focus:border-[#FFD700] focus:outline-none"
              data-testid="inv-reason-select"
            >
              <option value="manual_adjustment">Manual Adjustment</option>
              <option value="physical_count">Physical Count</option>
              <option value="new_stock_arrival">New Stock Arrival</option>
              <option value="correction">Correction</option>
              <option value="damaged_stock">Damaged Stock</option>
            </select>
          </div>

          {/* Save + Audit */}
          <div className="mt-6 flex gap-3">
            {enforcementSetting?.setup?.value ? (
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#FFD700] text-black font-heading py-3 px-8 tracking-wider hover:bg-[#FFC000] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="inv-save-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? "SAVING..." : "SAVE INVENTORY"}
              </button>
            ) : (
              <div className="flex items-center gap-3 px-6 py-3 border border-white/10 text-[#666]">
                <Lock className="w-4 h-4" />
                <span className="font-mono text-xs">INVENTORY SETUP LOCKED — Read-only mode</span>
              </div>
            )}
            <button
              onClick={() => loadProductAudit(selectedProduct.id)}
              className="border border-white/20 font-mono text-xs px-4 py-3 tracking-wider hover:border-[#FFD700] hover:text-[#FFD700] transition-colors"
              data-testid="inv-audit-btn"
            >
              VIEW CHANGE HISTORY
            </button>
          </div>

          {/* Product Audit Log */}
          {productAudit && (
            <div className="mt-6 border-t border-white/10 pt-4">
              <p className="font-mono text-xs text-[#FFD700] mb-3">CHANGE HISTORY — {selectedProduct.name}</p>
              {productAudit.length === 0 ? (
                <p className="text-[#A3A3A3] font-mono text-xs">No changes recorded</p>
              ) : (
                <div className="space-y-2">
                  {productAudit.map((l, i) => (
                    <div key={i} className="flex items-center gap-4 font-mono text-xs text-[#ccc] py-1 border-b border-white/5">
                      <span className="text-[#666] w-36">{new Date(l.timestamp).toLocaleString()}</span>
                      <span className="w-28">{l.color}/{l.size}</span>
                      <span className="w-16">{l.before} → {l.after}</span>
                      <span className={`w-12 ${l.change > 0 ? "text-green-500" : "text-red-500"}`}>{l.change > 0 ? "+" : ""}{l.change}</span>
                      <span className="text-[#666]">{l.reason}</span>
                      <span className="text-[#666]">{l.admin_email?.split("@")[0]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};
